import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import { ventrisConfig } from "../ventris.config";
import ClinicianIcon from "../icons/ClinicianIcon";
import DirectoryIcon from "../icons/DirectoryIcon";
import GroupIcon from "../icons/GroupIcon";
import SchoolStudentIcon from "../icons/SchoolStudentIcon";
import StudentIcon from "../icons/StudentIcon";
import TeacherIcon from "../icons/TeacherIcon";
import { getUseCaseTerminology } from "../useCase";
import { userHasResultsAccess } from "../assessmentAccess";
import AsyncStatus from "./AsyncStatus";

type HierarchyUser = {
  id: number;
  first_name: string;
  last_name: string;
  name: string;
  email: string;
  role: string;
  primary_assignment?: boolean;
};

type HierarchyStudent = {
  id: number;
  first_name: string;
  last_name: string;
  name: string;
  student_code: string;
  grade: string;
  archived: boolean;
  primary_assignment?: boolean;
};

export type HierarchyOrganization = {
  id: number;
  name: string;
  schoolday_type?: string | null;
  subscription_key?: string | null;
  starts_at?: string | null;
  expiration_date?: string | null;
  total_seats?: number | null;
  assessment_access?: string[] | null;
  effective_assessment_access?: string[];
  assessment_access_inherited?: boolean;
  use_case: "School" | "SLP";
  parent_organization_id: number | null;
  parent_organization_name: string;
  schoolday_managed: boolean;
  schoolday_managed_by_ancestor: boolean;
  can_edit_integration_settings?: boolean;
  child_organization_count?: number;
  child_class_count?: number;
  user_count?: number;
  student_count?: number;
  has_children?: boolean;
  children_loaded?: boolean;
  users: HierarchyUser[];
  students: HierarchyStudent[];
  children: HierarchyOrganization[];
};

type OrganizationHierarchyViewProps = {
  active: boolean;
  search: string;
  onEditOrganization: (organization: HierarchyOrganization) => void;
  onEditUser: (user: HierarchyUser & { organization_id: number }) => void;
  onEditStudent: (student: HierarchyStudent & { organization_id: number }) => void;
  canEditOrganization?: (organization: HierarchyOrganization) => boolean;
  onHierarchyChanged: () => void;
};

type HierarchyCounts = {
  organizationCount: number;
  classCount: number;
  users: HierarchyUser[];
  students: HierarchyStudent[];
};

function uniqueById<T extends { id: number }>(items: T[]) {
  return Array.from(new Map(items.map((item) => [item.id, item])).values());
}

function OrganizationHierarchyView({
  active,
  search,
  onEditOrganization,
  onEditUser,
  onEditStudent,
  canEditOrganization = () => true,
  onHierarchyChanged,
}: OrganizationHierarchyViewProps) {
  const terms = getUseCaseTerminology();
  const navigate = useNavigate();
  const [hierarchy, setHierarchy] = useState<HierarchyOrganization[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [loadingNodeIds, setLoadingNodeIds] = useState<number[]>([]);
  const [syncing, setSyncing] = useState(false);

  const allOrganizationKeys = (organizations: HierarchyOrganization[]): string[] => {
    return organizations.flatMap((organization) => [
      `org-${organization.id}`,
      ...allOrganizationKeys(organization.children || []),
    ]);
  };

  const loadHierarchy = async () => {
    const token = sessionStorage.getItem("token");
    setLoading(true);
    setError("");

    try {
      const response = await fetch(ventrisConfig.url + "/api/organization-hierarchy/children", {
        method: "get",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      });
      const data = await response.json();

      if (!data.success) {
        setError(data.message || "We couldn't load the organizational hierarchy.");
        return;
      }

      const organizations = data.organizations || [];
      setHierarchy(organizations);
      setExpandedKeys([]);
      setLoaded(true);
    } catch (loadError) {
      console.error(loadError);
      setError("We couldn't load the organizational hierarchy.");
    } finally {
      setLoading(false);
    }
  };

  const updateOrganizationNode = (
    organizations: HierarchyOrganization[],
    organizationId: number,
    updater: (organization: HierarchyOrganization) => HierarchyOrganization,
  ): HierarchyOrganization[] => {
    return organizations.map((organization) => {
      if (organization.id === organizationId) {
        return updater(organization);
      }

      return {
        ...organization,
        children: updateOrganizationNode(organization.children || [], organizationId, updater),
      };
    });
  };

  const loadOrganizationChildren = async (organizationId: number) => {
    if (loadingNodeIds.includes(organizationId)) {
      return;
    }

    const token = sessionStorage.getItem("token");
    setLoadingNodeIds((current) => [...current, organizationId]);
    setError("");

    try {
      const response = await fetch(`${ventrisConfig.url}/api/organization-hierarchy/children?parent_id=${organizationId}`, {
        method: "get",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      });
      const data = await response.json();

      if (!data.success) {
        setError(data.message || "We couldn't load this part of the organizational hierarchy.");
        return;
      }

      setHierarchy((current) => updateOrganizationNode(current, organizationId, (organization) => ({
        ...organization,
        children: data.organizations || [],
        users: data.users || [],
        students: data.students || [],
        children_loaded: true,
        has_children: ((data.organizations || []).length + (data.users || []).length + (data.students || []).length) > 0,
      })));
    } catch (loadError) {
      console.error(loadError);
      setError("We couldn't load this part of the organizational hierarchy.");
    } finally {
      setLoadingNodeIds((current) => current.filter((id) => id !== organizationId));
    }
  };

  useEffect(() => {
    if (active && !loaded && !loading) {
      loadHierarchy();
    }
  }, [active, loaded, loading]);

  const collectCounts = (organization: HierarchyOrganization): HierarchyCounts => {
    if (!organization.children_loaded) {
      return {
        organizationCount: organization.child_organization_count || 0,
        classCount: organization.child_class_count || 0,
        users: Array.from({ length: organization.user_count || 0 }, (_, index) => ({
          id: -1 - index,
          first_name: "",
          last_name: "",
          name: "",
          email: "",
          role: "",
        })),
        students: Array.from({ length: organization.student_count || 0 }, (_, index) => ({
          id: -1 - index,
          first_name: "",
          last_name: "",
          name: "",
          student_code: "",
          grade: "",
          archived: false,
        })),
      };
    }

    const descendantCounts = organization.children.map(collectCounts);
    const childOrganizations = organization.children.filter((child) => (child.schoolday_type || "").toLowerCase() !== "class");
    const childClasses = organization.children.filter((child) => (child.schoolday_type || "").toLowerCase() === "class");

    return {
      organizationCount: childOrganizations.length + descendantCounts.reduce((sum, count) => sum + count.organizationCount, 0),
      classCount: childClasses.length + descendantCounts.reduce((sum, count) => sum + count.classCount, 0),
      users: uniqueById([
        ...organization.users.filter((user) => user.primary_assignment !== false),
        ...descendantCounts.flatMap((count) => count.users),
      ]),
      students: uniqueById([
        ...organization.students.filter((student) => student.primary_assignment !== false),
        ...descendantCounts.flatMap((count) => count.students),
      ]),
    };
  };

  const hierarchyCounts = useMemo(() => {
    const counts = new Map<number, HierarchyCounts>();
    const visit = (organizations: HierarchyOrganization[]) => {
      organizations.forEach((organization) => {
        counts.set(organization.id, collectCounts(organization));
        visit(organization.children || []);
      });
    };
    visit(hierarchy);
    return counts;
  }, [hierarchy]);

  const filteredHierarchy = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) {
      return hierarchy;
    }

    const filterOrganizations = (organizations: HierarchyOrganization[]): HierarchyOrganization[] => {
      return organizations.flatMap((organization) => {
        const matchingChildren = filterOrganizations(organization.children || []);
        const matchingUsers = organization.users.filter((user) =>
          `${user.name} ${user.email}`.toLowerCase().includes(normalizedSearch)
        );
        const matchingStudents = organization.students.filter((student) =>
          `${student.name} ${student.student_code}`.toLowerCase().includes(normalizedSearch)
        );
        const organizationMatches = organization.name.toLowerCase().includes(normalizedSearch);

        if (!organizationMatches && matchingChildren.length === 0 && matchingUsers.length === 0 && matchingStudents.length === 0) {
          return [];
        }

        return [{
          ...organization,
          children: organizationMatches ? organization.children : matchingChildren,
          users: organizationMatches ? organization.users : matchingUsers,
          students: organizationMatches ? organization.students : matchingStudents,
        }];
      });
    };

    return filterOrganizations(hierarchy);
  }, [hierarchy, search]);

  useEffect(() => {
    if (search.trim()) {
      setExpandedKeys(allOrganizationKeys(filteredHierarchy));
    }
  }, [filteredHierarchy, search]);

  const toggleExpanded = (organization: HierarchyOrganization) => {
    const key = `org-${organization.id}`;
    const isExpanded = expandedKeys.includes(key);

    if (!isExpanded && !organization.children_loaded) {
      loadOrganizationChildren(organization.id);
    }

    setExpandedKeys((current) => current.includes(key)
      ? current.filter((item) => item !== key)
      : [...current, key]
    );
  };

  const organizationType = (organization: HierarchyOrganization) => {
    const type = (organization.schoolday_type || "").toLowerCase();

    if (terms.useCase === "SLP") {
      if (type === "class") return "caseload";
      if (type === "school") return "location";
      return "organization";
    }

    if (type === "district" || type === "school" || type === "class") {
      return type;
    }

    return "organization";
  };

  const organizationTypeLabel = (organization: HierarchyOrganization) => {
    const type = organizationType(organization);
    return type === "caseload" ? terms.class : type.charAt(0).toUpperCase() + type.slice(1);
  };

  const organizationIcon = (organization: HierarchyOrganization) => {
    const type = organizationType(organization);
    if (type === "class" || type === "caseload") return <GroupIcon />;
    return <DirectoryIcon />;
  };

  const pluralize = (count: number, singular: string, plural = `${singular}s`) => `${count} ${count === 1 ? singular : plural}`;

  const organizationSummary = (organization: HierarchyOrganization) => {
    const counts = hierarchyCounts.get(organization.id) || {
      organizationCount: 0,
      classCount: 0,
      users: [],
      students: [],
    };
    const type = organizationType(organization);

    if (type === "class" || type === "caseload") {
      return [
        pluralize(counts.users.length, terms.teacher, terms.teachers),
        pluralize(counts.students.length, terms.student, terms.students),
      ].join(", ");
    }

    const summary = [
      pluralize(counts.classCount, terms.class, terms.classes),
      pluralize(counts.users.length, terms.teacher, terms.teachers),
      pluralize(counts.students.length, terms.student, terms.students),
    ];

    if (type === "district") {
      summary.unshift(pluralize(counts.organizationCount, "School"));
    } else if (type === "organization") {
      summary.unshift(pluralize(counts.organizationCount, terms.useCase === "SLP" ? "Location" : "Organization"));
    }

    return summary.join(", ");
  };

  const resultsUrl = (studentIds: Array<number | string>, classId?: number) => {
    const params = new URLSearchParams();
    if (classId) {
      params.set("class", String(classId));
    } else if (studentIds.length > 0) {
      params.set("students", studentIds.map(String).join(","));
    }
    return `/results?${params.toString()}`;
  };

  const viewOrganizationResults = async (organization: HierarchyOrganization) => {
    const type = organizationType(organization);
    if (type === "class" || type === "caseload") {
      navigate(resultsUrl([], organization.id));
      return;
    }

    try {
      const response = await fetch(`${ventrisConfig.url}/api/organization-hierarchy/${organization.id}/student-ids`, {
        method: "get",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + sessionStorage.getItem("token"),
        },
      });
      const data = await response.json();
      navigate(resultsUrl(data.success ? (data.student_ids || []) : []));
    } catch (resultsError) {
      console.error(resultsError);
      navigate(resultsUrl([]));
    }
  };

  const syncSchoolDay = async () => {
    const rootOrganization = hierarchy.find((organization) => organization.schoolday_managed);
    if (!rootOrganization) {
      return;
    }

    setSyncing(true);
    setError("");
    try {
      const response = await fetch(ventrisConfig.url + `/api/organization/${rootOrganization.id}/schoolday-sync`, {
        method: "post",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + sessionStorage.getItem("token"),
        },
      });
      const data = await response.json();
      if (!data.success) {
        setError(data.message || "SchoolDay sync failed.");
        return;
      }
      await loadHierarchy();
      onHierarchyChanged();
    } catch {
      setError("SchoolDay sync failed.");
    } finally {
      setSyncing(false);
    }
  };

  const renderActions = (
    editDisabled: boolean,
    onEdit: () => void,
    onResults: () => void,
  ) => (
    <div className="organization-tree-actions">
      <Button variant="link" onClick={onEdit} disabled={editDisabled}>Edit</Button>
      {userHasResultsAccess(JSON.parse(sessionStorage.getItem("current_user") || "{}")) ? <>
        <span aria-hidden="true">|</span>
        <Button variant="link" onClick={onResults}>View Results</Button>
      </> : null}
    </div>
  );

  const renderOrganizationNode = (organization: HierarchyOrganization, depth = 0): JSX.Element => {
    const key = `org-${organization.id}`;
    const isExpanded = expandedKeys.includes(key);
    const isLoadingNode = loadingNodeIds.includes(organization.id);
    const primaryUsers = organization.users.filter((user) => user.primary_assignment !== false);
    const primaryStudents = organization.students.filter((student) => student.primary_assignment !== false);
    const hasNestedContent = organization.children_loaded
      ? organization.children.length > 0 || primaryUsers.length > 0 || primaryStudents.length > 0
      : organization.has_children || (organization.child_organization_count || 0) > 0 || (organization.user_count || 0) > 0 || (organization.student_count || 0) > 0;
    const type = organizationType(organization);

    return (
      <div key={key} className="organization-tree-node">
        <div className="organization-tree-row" style={{ marginLeft: `${depth * 50}px` }}>
          <button
            type="button"
            className={`organization-tree-toggle ${hasNestedContent ? "" : "organization-tree-toggle-empty"}`}
            onClick={() => hasNestedContent && toggleExpanded(organization)}
            aria-label={isExpanded ? "Collapse organization" : "Expand organization"}
            disabled={!hasNestedContent || isLoadingNode}
          >
            {isLoadingNode ? "…" : (hasNestedContent ? (isExpanded ? "−" : "+") : "")}
          </button>
          <div className={`organization-tree-card organization-tree-card-${type}`}>
            <div className="organization-tree-content">
              <div className="organization-tree-title-row">
                <div className="organization-tree-title">
                  {organizationIcon(organization)}
                  <span>{organization.name}</span>
                  <span className={`organization-tree-badge organization-tree-badge-${type}`}>{organizationTypeLabel(organization)}</span>
                  {organization.schoolday_managed ? <span className="organization-tree-badge organization-tree-badge-schoolday">SchoolDay</span> : null}
                </div>
              </div>
              <div className="organization-tree-meta">{organizationSummary(organization)}</div>
            </div>
            {renderActions(
              !canEditOrganization(organization),
              () => onEditOrganization(organization),
              () => viewOrganizationResults(organization),
            )}
          </div>
        </div>
        {isExpanded ? (
          <div className="organization-tree-children">
            {isLoadingNode ? (
              <div className="organization-tree-row organization-tree-leaf-row" style={{ marginLeft: `${(depth + 1) * 50}px` }}>
                <div className="organization-tree-toggle organization-tree-toggle-empty" />
                <div className="organization-tree-loading-node"><Spinner animation="border" size="sm" /> Loading...</div>
              </div>
            ) : null}
            {organization.children.map((child) => renderOrganizationNode(child, depth + 1))}
            {primaryUsers.map((user) => (
              <div key={`user-${user.id}`} className="organization-tree-row organization-tree-leaf-row" style={{ marginLeft: `${(depth + 1) * 50}px` }}>
                <div className="organization-tree-toggle organization-tree-toggle-empty" />
                <div className="organization-tree-leaf organization-tree-leaf-user">
                  <div className="organization-tree-content">
                    <div className="organization-tree-title">
                      {terms.useCase === "SLP" ? <ClinicianIcon /> : <TeacherIcon />}
                      <span>{user.name || user.email}</span>
                      <span className="organization-tree-badge organization-tree-badge-user">{user.role === "Admin" ? "Admin" : terms.teacher}</span>
                      {organization.schoolday_managed ? <span className="organization-tree-badge organization-tree-badge-schoolday">SchoolDay</span> : null}
                    </div>
                    <div className="organization-tree-meta">{pluralize(primaryStudents.length, terms.student, terms.students)}</div>
                  </div>
                  {renderActions(
                    organization.schoolday_managed,
                    () => onEditUser({ ...user, organization_id: organization.id }),
                    () => navigate(resultsUrl(primaryStudents.map((student) => student.id))),
                  )}
                </div>
              </div>
            ))}
            {primaryStudents.map((student) => (
              <div key={`student-${student.id}`} className="organization-tree-row organization-tree-leaf-row" style={{ marginLeft: `${(depth + 1) * 50}px` }}>
                <div className="organization-tree-toggle organization-tree-toggle-empty" />
                <div className="organization-tree-leaf organization-tree-leaf-student">
                  <div className="organization-tree-content">
                    <div className="organization-tree-title">
                      {terms.useCase === "School" ? <SchoolStudentIcon /> : <StudentIcon />}
                      <span>{student.student_code || student.name}</span>
                      <span className="organization-tree-badge organization-tree-badge-student">{terms.student}</span>
                      {organization.schoolday_managed ? <span className="organization-tree-badge organization-tree-badge-schoolday">SchoolDay</span> : null}
                    </div>
                    <div className="organization-tree-meta">{student.name}{student.grade ? `, Grade ${student.grade}` : ""}</div>
                  </div>
                  {renderActions(
                    organization.schoolday_managed,
                    () => onEditStudent({ ...student, organization_id: organization.id }),
                    () => navigate(resultsUrl([student.id])),
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  if (!active) {
    return null;
  }

  const schoolDayManaged = hierarchy.some((organization) => organization.schoolday_managed);

  return (
    <div className="organization-hierarchy-view" aria-busy={loading || syncing || loadingNodeIds.length > 0}>
      <AsyncStatus
        busy={loading || syncing || loadingNodeIds.length > 0}
        busyMessage={syncing ? "Refreshing directory data." : "Loading directory data."}
        completeMessage="Directory data loaded."
      />
      <div className="organization-hierarchy-header">
        <div>
          <h3 className="organization-hierarchy-title">Organizational Hierarchy</h3>
          <p className="organization-hierarchy-subtitle">
            {schoolDayManaged
              ? <>Your directory is managed by SchoolDay integration. Refresh data to sync the most current information. Please contact <strong>Ventris Learning</strong> to make manual edits.</>
              : <>This directory shows the organizational hierarchy of your {terms.useCase === "SLP" ? "organization" : "system"}. Click edit to make changes to any {terms.useCase === "SLP" ? `organization, location, ${terms.class.toLowerCase()}, ${terms.teacher.toLowerCase()}, or ${terms.student.toLowerCase()}` : `school, ${terms.class.toLowerCase()}, ${terms.teacher.toLowerCase()}, or ${terms.student.toLowerCase()}`}. Click view results to view test results by that level.</>}
          </p>
        </div>
        {schoolDayManaged ? (
          <Button className="ventris-button-primary ventris-button-primary-blue" onClick={syncSchoolDay} disabled={syncing}>
            {syncing ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Refreshing...</> : "Refresh Data"}
          </Button>
        ) : null}
      </div>

      <div className="organization-hierarchy-toolbar">
        <Button variant="link" onClick={() => setExpandedKeys([])} disabled={!loaded || hierarchy.length === 0}>Collapse All</Button>
        <Button variant="link" onClick={() => setExpandedKeys(allOrganizationKeys(hierarchy))} disabled={!loaded || hierarchy.length === 0}>Expand Loaded</Button>
      </div>

      {error ? <Alert variant="danger" className="mb-3">{error}</Alert> : null}
      {loading && !loaded ? <div className="organization-hierarchy-loading"><Spinner animation="border" variant="secondary" /></div> : null}
      {!loading && loaded && filteredHierarchy.length === 0 ? <Alert variant="light" className="mb-0">No directory entries match your search.</Alert> : null}
      {!loading && filteredHierarchy.length > 0 ? (
        <div className="organization-tree">
          {filteredHierarchy.map((organization) => renderOrganizationNode(organization))}
        </div>
      ) : null}
    </div>
  );
}

export default OrganizationHierarchyView;
